export type InstallChoice = 'accepted' | 'dismissed' | 'unavailable';

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredPrompt: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as InstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

export function isPwaInstallHandoff(search = window.location.search) {
  return new URLSearchParams(search).get('install') === '1';
}

export function isStandalonePwa() {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || navigatorWithStandalone.standalone === true;
}

export function isIosBrowser() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function hasInstallPrompt() {
  return deferredPrompt !== null;
}

export function subscribeInstallPrompt(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export async function preparePwaInstall() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js?v=pwa-install-app-v1', { scope: '/' });
    await navigator.serviceWorker.ready;
    return registration;
  } catch {
    return null;
  }
}

export async function promptPwaInstall(): Promise<InstallChoice> {
  if (!deferredPrompt) return 'unavailable';
  const prompt = deferredPrompt;
  deferredPrompt = null;
  notify();
  await prompt.prompt();
  const choice = await prompt.userChoice;
  return choice.outcome;
}

export function clearInstallHandoff() {
  const url = new URL(window.location.href);
  url.searchParams.delete('install');
  history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
}
